/**
 * Maps axios/network failures to user-friendly messages.
 * Axios reports "Network Error" when the server is unreachable or times out —
 * not when the user's internet is down.
 */

const RATE_LIMITED_MESSAGE =
  "You're going a bit fast — please wait a moment and try again.";
const SERVER_BUSY_MESSAGE =
  "The server is busy right now. Please wait a moment and try again.";

export type ApiErrorDisplay = {
  title: string;
  message: string;
  retryable: boolean;
};

/**
 * Errors reach here in several shapes: a raw AxiosError, the plain
 * `{message, statusCode}` object our api/ modules reject with, or a bare Error.
 */
export function getStatus(error: unknown): number | undefined {
  const e = error as {
    response?: { status?: number };
    statusCode?: number;
  };
  return e?.response?.status ?? e?.statusCode ?? undefined;
}

export function normalizeApiMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  // Callers sometimes stash a bare string in React state; treat it as the message.
  if (typeof error === "string" && error.trim()) return error.trim();
  const e = error as {
    message?: string | string[];
    response?: { data?: { message?: string | string[] } };
  };
  const raw = e?.message ?? e?.response?.data?.message;
  if (Array.isArray(raw)) return raw.join(" ");
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return getApiErrorMessage(error, fallback);
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const message =
    (error as { message?: string })?.message?.trim() ||
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message?.trim() ||
    "";

  const lower = message.toLowerCase();

  // Rate limited. Without this the backend's raw "ThrottlerException: Too Many
  // Requests" would be shown to the user verbatim.
  if (getStatus(error) === 429 || lower.includes("throttlerexception")) {
    return RATE_LIMITED_MESSAGE;
  }

  if (
    lower === "network error" ||
    lower.includes("timeout") ||
    lower.includes("econnaborted") ||
    lower.includes("econnrefused")
  ) {
    return SERVER_BUSY_MESSAGE;
  }

  return message || fallback;
}

export function isRetryableApiError(error: unknown): boolean {
  const status = getStatus(error);
  const message = getApiErrorMessage(error, "");
  return (
    message === RATE_LIMITED_MESSAGE ||
    message === SERVER_BUSY_MESSAGE ||
    status === 0 ||
    status === 429 ||
    (status != null && status >= 500)
  );
}

export function getMaterialErrorDisplay(
  error: unknown,
  hasMaterial: boolean,
): ApiErrorDisplay {
  if (!hasMaterial && !error) {
    return {
      title: "Material Not Found",
      message:
        "The learning material you are trying to access is not available or does not exist.",
      retryable: false,
    };
  }

  const status = getStatus(error);
  const message = normalizeApiMessage(
    error,
    "Failed to load material details.",
  );

  if (status === 404) {
    return {
      title: "Material Not Found",
      message,
      retryable: false,
    };
  }

  if (status === 401 || status === 403) {
    return {
      title: "Access Denied",
      message:
        "Your session may have expired or you do not have access to this material. Please log in again.",
      retryable: false,
    };
  }

  if (isRetryableApiError(error)) {
    return {
      title: "Unable to Load Content",
      message,
      retryable: true,
    };
  }

  return {
    title: "Unable to Load Content",
    message,
    retryable: true,
  };
}

export function getTestErrorDisplay(error: unknown): ApiErrorDisplay {
  const message = normalizeApiMessage(error, "The test could not be started.");
  const lower = message.toLowerCase();

  if (lower.includes("maximum attempts")) {
    return {
      title: "Maximum Attempts Reached",
      message,
      retryable: false,
    };
  }

  if (
    lower.includes("complete all course materials") ||
    lower.includes("complete the required course materials") ||
    lower.includes("complete the learning material") ||
    lower.includes("must complete")
  ) {
    return {
      title: "Complete Materials First",
      message,
      retryable: false,
    };
  }

  if (lower.includes("not active")) {
    return {
      title: "Assessment Unavailable",
      message,
      retryable: false,
    };
  }

  if (isRetryableApiError(error)) {
    return {
      title: "Unable to Start Test",
      message,
      retryable: true,
    };
  }

  return {
    title: "Test Not Available",
    message,
    retryable: false,
  };
}
